import { Grid, IconButton, Menu, MenuItem, Stack, Typography, Slide } from '@mui/material';
import { useCallback, useMemo, useRef, useState } from 'react';
import { IotTargetIcon } from 'src/components/iot/icons';
import { DoorsMenu } from './DoorsMenu';

type Props = {
  camera: any;
  cameras: any[];
  doors: any[];
  unshownDevices: any[];
  index: number;
  moveToTag: (camera: any, param: boolean) => void;
  setSelectedIndex: (index: number | null) => void;
  handleCameraSelect: (device: any) => void;
  removeCameraFromGrid: (camera: any) => void;
};

export const CameraGridItem = ({
  camera,
  cameras,
  index,
  moveToTag,
  setSelectedIndex,
  unshownDevices,
  handleCameraSelect,
  removeCameraFromGrid,
  doors
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<null | any>(null);
  const [hovered, setHovered] = useState(false);
  const open = Boolean(anchorEl);
  const containerRef = useRef<HTMLDivElement>(null);

  const doorsRelatedToCamera = useMemo(() => {
    const relatedDoorsIds = cameras
      ?.find((c) => c.id === camera.id)
      ?.relatedAssets?.filter((asset) => asset.type === 'door')
      ?.map((asset) => asset.id);
    const doorsFullInfo = doors?.filter((door) => relatedDoorsIds?.includes(door.id));
    return doorsFullInfo;
  }, [camera, cameras, doors]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>, index: number) => {
    setAnchorEl(event.currentTarget);
    setSelectedIndex(index);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedIndex(null);
  };

  const moveToTagFromGrid = useCallback(
    (camera: any) => {
      const foundCamera = cameras?.find((c) => c.id === camera.id);
      if (foundCamera) {
        const relatedTag = foundCamera?.relatedAssets?.find((a) => a.type === 'tag');
        if (relatedTag) {
          moveToTag({ sid: relatedTag.id }, true);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cameras]
  );

  return (
    <Grid
      item
      xs={4}
      key={index}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Stack
        alignItems="center"
        justifyContent="center"
        overflow="hidden"
        width="100%"
        height="360px"
        position="relative"
        sx={{
          borderRadius: '8px',
          background: 'rgba(0, 0, 0, 0.60)',
          backdropFilter: 'blur(20px)'
        }}
        ref={containerRef}
      >
        {camera?.url ? (
          <>
            <iframe
              src={camera.url}
              allow="autoplay; fullscreen"
              allowFullScreen
              title="Embedded video"
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                background:
                  'linear-gradient(0deg, rgba(0, 0, 0, 0.80) 0%, rgba(0, 0, 0, 0.00) 100%), lightgray'
              }}
            />
            {camera?.name && (
              <Slide in={!hovered} direction="up">
                <Typography
                  sx={{
                    position: 'absolute',
                    bottom: '20px',
                    left: '20px',
                    fontFamily: 'Inter',
                    fontSize: '18px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    p: '10px'
                  }}
                >
                  {camera?.name}
                </Typography>
              </Slide>
            )}
            {hovered && (
              <Slide in={hovered} container={containerRef.current} direction="up">
                <Stack
                  position="absolute"
                  bottom={0}
                  left={0}
                  gap={2}
                  p="20px"
                  direction="row"
                  zIndex={1000}
                  width="100%"
                  justifyContent="space-between"
                >
                  <Stack direction="row" gap="10px" alignItems="center" justifyContent="center">
                    {doorsRelatedToCamera?.length > 0 && (
                      <DoorsMenu doorsRelatedToCamera={doorsRelatedToCamera} />
                    )}
                    <IconButton
                      onClick={() => moveToTagFromGrid(camera)}
                      sx={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        background: 'rgba(255, 255, 255, 0.10)',
                        backdropFilter: 'blur(20px)',
                        cursor: 'pointer'
                      }}
                    >
                      <IotTargetIcon />
                    </IconButton>
                  </Stack>
                  <IconButton
                    onClick={() => removeCameraFromGrid(camera)}
                    sx={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      background: 'rgba(255, 255, 255, 0.10)',
                      backdropFilter: 'blur(20px)',
                      cursor: 'pointer',
                      color: '#fff'
                    }}
                  >
                    <i className="fas fa-times" />
                  </IconButton>
                </Stack>
              </Slide>
            )}
          </>
        ) : (
          <>
            <IconButton
              onClick={(e) => {
                handleClick(e, index);
              }}
              sx={{
                width: '48px',
                height: '48px',
                borderRadius: '8px',
                background: 'rgba(255, 255, 255, 0.10)',
                backdropFilter: 'blur(20px)',
                color: '#fff'
              }}
            >
              <i className="fa-regular fa-plus" />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleCloseMenu}
              PaperProps={{
                style: {
                  boxShadow: 'none',
                  color: '#fff',
                  background: 'rgba(0, 0, 0, 0.60)'
                }
              }}
            >
              {unshownDevices && unshownDevices?.length > 0 ? (
                unshownDevices?.map((device) => {
                  return (
                    <MenuItem
                      key={device.id}
                      onClick={() => {
                        handleCloseMenu();
                        handleCameraSelect(device);
                      }}
                    >
                      {device.name}
                    </MenuItem>
                  );
                })
              ) : (
                <MenuItem onClick={handleCloseMenu}>No items</MenuItem>
              )}
            </Menu>
          </>
        )}
      </Stack>
    </Grid>
  );
};
